import { Box } from '@mui/material';
import React, { forwardRef, useCallback } from 'react';

import SlideUpList from '../../../animations/SlideUpList';
import { InventoryItem } from '../../../components';
import { PER_PAGE_OPTIONS } from '../../../constants/table';
import useClearFiltersCallback from '../../../hooks/useClearFiltersCallback';
import { IInventoryItem } from '../../../types/inventories';
import { IFiltersRef } from '../../../types/tables';
import { getMskuItemsMaxCount, isItemMsku } from '../../../utils/inventories';
import PaginatedList, { IPaginatedListChangeEvent, IPaginatedListRef } from '../../PaginatedList/PaginatedList';
import { PageContent, PaginationControls, TableFilteredEmptyPlaceholder, TableViewLoader } from '../../_mui';

import styles from './InventoryGridView.module.scss';

interface IProps {
  totalCount: number;
  onChange: (event: IPaginatedListChangeEvent) => void;
  isFinalItems: boolean;
  isLoading: boolean;
  items: IInventoryItem[];
  canEditInventory: boolean;
  onEditClick: (item: IInventoryItem) => void;
  onDeleteClick: (itemId: string) => void;
  onItemClick: (item: IInventoryItem) => void;
  filtersRef?: React.RefObject<IFiltersRef>;
  pageSize: number;
  onPageSizeChange?: (newPageSize: number) => void;
  page?: number;
}

const InventoryGridView = forwardRef<IPaginatedListRef<IInventoryItem>, IProps>(
  (
    {
      items,
      totalCount,
      onChange,
      isFinalItems,
      isLoading,
      canEditInventory,
      onItemClick,
      onEditClick,
      onDeleteClick,
      filtersRef,
      pageSize,
      onPageSizeChange,
      page,
    },
    paginatedListRef,
  ) => {
    const handleClearFilters = useClearFiltersCallback(filtersRef ?? null);

    const renderItems = useCallback(
      (inventory: IInventoryItem[]) => {
        const isInventoryVisible = inventory && inventory.length > 0;

        if (!isInventoryVisible) {
          return <TableFilteredEmptyPlaceholder itemType="inventory items" onClearFilters={handleClearFilters} />;
        }

        return (
          <PageContent sx={{ pb: '0 !important' }}>
            <div className={styles.list}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  columnGap: 3,
                }}
              >
                <SlideUpList
                  items={inventory.map((item: IInventoryItem) => {
                    const {
                      item_id,
                      name,
                      price,
                      currency_code,
                      count,
                      image_url,
                      sku_options,
                      type,
                      item_customization_allowed: isCustomizable,
                      fulfillment_center_id,
                      preorder_allowed,
                      low_stock_threshold,
                    } = item;

                    const isMsku = isItemMsku(item);
                    const itemCount = isMsku ? getMskuItemsMaxCount(sku_options ?? []) : count ?? 0;

                    return {
                      key: item_id,
                      renderer: (
                        <div className={styles.item}>
                          <InventoryItem
                            fulfillment_center_id={fulfillment_center_id}
                            itemId={item_id}
                            type={type}
                            title={name}
                            price={price}
                            currency_code={currency_code}
                            icon={image_url}
                            count={itemCount}
                            isPreorder={preorder_allowed}
                            skuOptions={sku_options}
                            threshold={low_stock_threshold}
                            className={styles.wide}
                            infoClassName={styles.itemInfo}
                            iconClassName={styles.icon}
                            isCustomizable={isCustomizable}
                            onClick={() => onItemClick(item)}
                            {...(canEditInventory
                              ? {
                                  onDelete: () => onDeleteClick(item_id),
                                  onEdit: () => onEditClick(item),
                                }
                              : {})}
                          />
                        </div>
                      ),
                    };
                  })}
                />
              </Box>
            </div>
          </PageContent>
        );
      },
      [canEditInventory, handleClearFilters, onDeleteClick, onEditClick, onItemClick],
    );

    return (
      <Box className={styles.gridViewContainer}>
        <PaginatedList<IInventoryItem>
          ref={paginatedListRef}
          items={items}
          pageSize={pageSize}
          totalCount={totalCount}
          onChange={onChange}
          isFinalItems={isFinalItems}
          defaultValue={page}
        >
          {({ items: inventory, pagination: Pagination }) => {
            const visibleItems = !isLoading && inventory && inventory.length > 0;

            return (
              <>
                {isLoading ? <TableViewLoader /> : renderItems(inventory)}
                {visibleItems && (
                  <PaginationControls
                    pageSize={pageSize}
                    options={PER_PAGE_OPTIONS}
                    onPageSizeChange={onPageSizeChange!}
                    PaginationComponent={Pagination}
                  />
                )}
              </>
            );
          }}
        </PaginatedList>
      </Box>
    );
  },
);

export default InventoryGridView;
